import { useEffect, useRef, useState, type JSX } from 'react';

import { useBesedila } from '../lib/jezik';

/** Razpon mesecev od–do (1–12). */
export interface RazponMesecev {
  od: number;
  do: number;
}

/**
 * Izbira mesecev nad poročilom (docs/ARHITEKTURA.md, 6.1).
 *
 * Enak meni kot »Vrednost« pri dimenziji: gumb s trenutno izbiro, v oknu
 * »(Vsi meseci)« in dvanajst mesecev s kljukicami, spodaj *V redu* /
 * *Prekliči*. Izbira je vedno **razpon od–do**: prvi klik izbere začetni
 * mesec, drugi končni, meseci vmes se obkljukajo sami – vidno, ne tiho.
 * Tretji klik začne novo izbiro. Pri bilanci s spremembami poljubni meseci
 * ne bi imeli pomena, zato vrzeli ni mogoče izbrati.
 *
 * Meseci za pokritostjo uvoza so sivi (»ni podatkov«); izbrati jih je mogoče,
 * poročilo pa pove, da niso prikazani. Dokler okno ni potrjeno, se poročilo
 * ne preračunava.
 */
export function IzbirnikMesecev({
  izbran,
  pokritost,
  naIzbiro,
}: {
  /** Razpon iz naslova strani; `undefined` = vsi meseci. */
  izbran: RazponMesecev | undefined;
  /** Do katerega meseca so podatki uvoženi; brez podatka nobeden ni siv. */
  pokritost: number | undefined;
  /** `undefined` = vsi meseci (izbira se odstrani). */
  naIzbiro: (razpon: RazponMesecev | undefined) => void;
}): JSX.Element {
  const b = useBesedila();
  const [odprt, setOdprt] = useState(false);
  const [osnutek, setOsnutek] = useState<RazponMesecev | undefined>(undefined);
  /** Po prvem kliku čaka na končni mesec. */
  const [cakaKonec, setCakaKonec] = useState(false);
  const okvir = useRef<HTMLDivElement>(null);

  // Klik zunaj okna in tipka Esc zapreta okno brez potrditve, kot pri dimenziji.
  useEffect(() => {
    if (!odprt) return undefined;
    const zapri = (d: MouseEvent): void => {
      if (okvir.current && !okvir.current.contains(d.target as Node)) setOdprt(false);
    };
    const naTipko = (d: KeyboardEvent): void => {
      if (d.key === 'Escape') setOdprt(false);
    };
    document.addEventListener('mousedown', zapri);
    document.addEventListener('keydown', naTipko);
    return () => {
      document.removeEventListener('mousedown', zapri);
      document.removeEventListener('keydown', naTipko);
    };
  }, [odprt]);

  const mesec = (m: number): string => b.meseci[m - 1] ?? String(m);
  const napis = (r: RazponMesecev | undefined): string =>
    r === undefined ? b.vsiMeseci : r.od === r.do ? mesec(r.od) : `${mesec(r.od)} – ${mesec(r.do)}`;

  function odpri(): void {
    setOsnutek(izbran);
    setCakaKonec(false);
    setOdprt(true);
  }

  /** Prvi klik začetek, drugi konec (vmesni se izberejo sami), tretji nova izbira. */
  function klik(m: number): void {
    if (osnutek !== undefined && cakaKonec) {
      setOsnutek({ od: Math.min(osnutek.od, m), do: Math.max(osnutek.od, m) });
      setCakaKonec(false);
    } else {
      setOsnutek({ od: m, do: m });
      setCakaKonec(true);
    }
  }

  function potrdi(): void {
    setOdprt(false);
    naIzbiro(osnutek);
  }

  const meseci = Array.from({ length: 12 }, (_, i) => i + 1);
  return (
    <div className="spustni izbirnik-mesecev" ref={okvir}>
      <span className="drobno">{b.meseciNaziv}</span>
      <button
        type="button"
        className="spustni-gumb"
        aria-expanded={odprt}
        aria-haspopup="dialog"
        onClick={() => {
          if (odprt) setOdprt(false);
          else odpri();
        }}
      >
        <span>{napis(izbran)}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {odprt && (
        <div className="spustni-panel" role="dialog" aria-label={b.meseciNaziv}>
          <ul className="seznam-vrednosti">
            <li className="vse">
              <label>
                <input
                  type="checkbox"
                  checked={osnutek === undefined}
                  onChange={() => {
                    setOsnutek(undefined);
                    setCakaKonec(false);
                  }}
                />
                <span className="ime">{b.vsiMeseci}</span>
              </label>
            </li>
            {meseci.map((m) => {
              const brezPodatkov = pokritost !== undefined && m > pokritost;
              return (
                <li key={m} className={brezPodatkov ? 'brez-podatkov' : undefined}>
                  <label>
                    <input
                      type="checkbox"
                      checked={osnutek !== undefined && m >= osnutek.od && m <= osnutek.do}
                      onChange={() => {
                        klik(m);
                      }}
                    />
                    <span className="ime">{mesec(m)}</span>
                    {brezPodatkov && <span className="drobno">{b.niPodatkov}</span>}
                  </label>
                </li>
              );
            })}
          </ul>
          <p className="drobno namig-mesecev">{cakaKonec ? b.izberiKoncniMesec : b.meseciZaporedni}</p>
          <div className="spustni-gumbi">
            <button type="button" className="gumb" onClick={potrdi}>
              {b.vRedu}
            </button>
            <button
              type="button"
              onClick={() => {
                setOdprt(false);
              }}
            >
              {b.preklici}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
